import { createContext, useContext, useEffect, useState } from 'react'

import { useResErrors, useStateCallback } from 'hooks'
import { clearObject } from 'lib/utils'
import { SalesService } from 'lib/api'
import { ALL, AllStatusesDeal, OKTELL, SMM } from 'constants/deals'
import moment from 'moment'
import { useAuthStore } from 'context/AuthStore'

export const WorListStoreContext = createContext({
  workListStore: {},
  workListStoreFunc: {}
})

const INIT_FILTER = {
  callResult: '',
  contactId: '',
  globalId: '',
  firstName: '',
  phoneNumber: '',
  saleType: '',
  vin: ''
}

const WorkListStoreProviderManager = ({ homePrData, ...props }) => {
  const { standardCheck } = useResErrors()
  const { authStore } = useAuthStore()

  const [list, setList] = useState([])
  const [loading, setLoading] = useState(false)
  const [selectedTab, setSelectedTab] = useStateCallback(ALL)
  const [filter, setFilter] = useStateCallback(INIT_FILTER)
  const [countsTab, setCountsTab] = useStateCallback({})
  const [pagination, setPagination] = useStateCallback({
    page: 0,
    pageCount: 0
  })

  useEffect(() => {
    getTabCounts()
  }, [])

  useEffect(() => {
    getListWl()()
  }, [filter])

  const onPaginationChange = page =>
    setPagination(
      prevState => ({ ...prevState, page }),
      updated => {
        getListWl(filter, updated)()
      }
    )

  const onChangeFilter = event => {
    const { name, value } = event.target

    if (name === 'createdDate' || name === 'updatedDate') {
      handleDateFilter(name, value)

      return
    }

    if (name === 'globalId') {
      setFilter({ ...filter, [name]: value?.toNum() })

      return
    }

    if (['firstName', 'phoneNumber'].includes(name)) {
      setFilter({ ...filter, client: { ...filter.client, [name]: value } })

      return
    }

    setFilter({ ...filter, [name]: value })
  }

  const handleDateFilter = (name, value) => {
    const dateFrom = value?.from
      ? moment(value.from).format('YYYY-MM-DD')
      : undefined
    const dateTo = value?.to ? moment(value.to).format('YYYY-MM-DD') : undefined
    const isCreatedDate = name === 'createdDate'

    return setFilter({
      ...filter,
      createdDateFrom: isCreatedDate ? dateFrom : undefined,
      createdDateTo: isCreatedDate ? dateTo : undefined,
      updatedDateFrom: !isCreatedDate ? dateFrom : undefined,
      updatedDateTo: !isCreatedDate ? dateTo : undefined
    })
  }

  const getListWl =
    (data = filter, pages = pagination, tab = selectedTab) =>
    async () => {
      setLoading(true)

      const filter = clearObject(data)
      const neededTab = AllStatusesDeal.find(item => item.code === tab)

      let myData = neededTab?.saleStatusTrackCode
        ? { ...filter, saleStatusTrackCode: neededTab?.saleStatusTrackCode }
        : { ...filter, statusCodes: tab === ALL ? undefined : [tab] }

      if (tab === OKTELL) {
        myData = {
          ...myData,
          branchCodes: [authStore?.user?.data?.branch?.code],
          creationType: ['oktell'],
          notAssigned: true,
          statusCodes: undefined
        }
      }

      if (tab === SMM) {
        myData = {
          ...myData,
          branchCodes: [authStore?.user?.data?.branch?.code],
          creationType: ['smm'],
          notAssigned: true,
          statusCodes: undefined
        }
      }

      if (filter?.callResult) {
        myData = {
          ...myData,
          branchCodes: [authStore?.user?.data?.branch?.code]
        }
      }

      await SalesService.getList(myData, 10, pages.page * 10).then(response =>
        standardCheck(response, () => {
          setList(response.data?.listSales || [])
          setPagination({
            ...pages,
            pageCount: Math.ceil(response.data?.countFiltered / 10) || 0
          })
        })
      )

      setLoading(false)
    }

  const getTabCounts = async () => {
    await SalesService.getStatusCounts().then(res =>
      standardCheck(res, () => {
        const { data } = res

        setCountsTab({
          [ALL]: data?.total || 0,
          ...data?.list?.reduce((acc, item) => {
            acc[item.code] = item.count
            return acc
          }, {})
        })
      })
    )
  }

  const changeTab = status => {
    setSelectedTab(status, s => {
      setPagination({ page: 0, pageCount: 0 }, () => {
        getListWl(filter, { page: 0, pageCount: 0 }, s)()
      })
    })
  }

  const workListStore = {
    list,
    loading,
    filter,
    pagination,
    branches: homePrData?.homeStore?.branchList,
    countsTab,
    selectedTab
  }

  const workListStoreFunc = {
    getListWl,
    onChangeFilter,
    onPaginationChange,
    changeTab,
    setLoading,
    setList,
    setPagination
  }

  return (
    <WorListStoreContext.Provider value={{ workListStore, workListStoreFunc }}>
      {props.children}
    </WorListStoreContext.Provider>
  )
}

export default WorkListStoreProviderManager

export const useWorkListStoreManager = () => useContext(WorListStoreContext)
